import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { BookingAgentService } from 'src/app/services/booking-agent/booking-agent.service';
import { BookingAgentModalService } from 'src/app/services/booking-agent-modal/booking-agent-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-booking-agents',
  templateUrl: './booking-agents.component.html',
  styleUrls: ['./booking-agents.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class BookingAgentsComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  bookingAgents: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedBookingAgents: string[] = [];

  constructor(
    private _bookingAgentService: BookingAgentService,
    private _bookingAgentModalService: BookingAgentModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadBookingAgents();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadBookingAgents(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedBookingAgents = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._bookingAgentService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.bookingAgents = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.bookingAgents = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading booking agents:', error);
          this.bookingAgents = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadBookingAgents(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadBookingAgents(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadBookingAgents();
  }

  openAddBookingAgentModal(): void {
    this._bookingAgentModalService.clearSelectedBookingAgent();
    const modalElement = document.getElementById('kt_modal_booking_agent_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(bookingAgent: any): void {
    this._bookingAgentModalService.setSelectedBookingAgent(bookingAgent);
    const modalElement = document.getElementById('kt_modal_booking_agent_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteBookingAgent(bookingAgent: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${bookingAgent.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(bookingAgent);
      }
    });
  }

  private performDelete(bookingAgent: any): void {
    const bookingAgentId = bookingAgent._id || bookingAgent.id;
    this.subscriptions.push(
      this._bookingAgentService.DeleteDataById(bookingAgentId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Booking agent has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadBookingAgents(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete booking agent.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting booking agent.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onBookingAgentAdded(): void {
    console.log('Booking agent added, refreshing list...');
    this.loadBookingAgents(this.currentPage, this.searchTerm, this.filters);
  }

  onBookingAgentUpdated(): void {
    console.log('Booking agent updated, refreshing list...');
    this.loadBookingAgents(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(bookingAgentId: string): boolean {
    return this.selectedBookingAgents.includes(bookingAgentId);
  }

  isAllSelected(): boolean {
    return this.bookingAgents.length > 0 && this.selectedBookingAgents.length === this.bookingAgents.length;
  }

  isIndeterminate(): boolean {
    return this.selectedBookingAgents.length > 0 && this.selectedBookingAgents.length < this.bookingAgents.length;
  }

  toggleSelection(bookingAgentId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedBookingAgents.includes(bookingAgentId)) {
        this.selectedBookingAgents.push(bookingAgentId);
      }
    } else {
      this.selectedBookingAgents = this.selectedBookingAgents.filter(id => id !== bookingAgentId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedBookingAgents = this.bookingAgents.map(bookingAgent => bookingAgent._id);
    } else {
      this.selectedBookingAgents = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-booking-agent-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-booking-agent-table-toolbar="selected"]');

    if (this.selectedBookingAgents.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedBookingAgents(): void {
    if (this.selectedBookingAgents.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedBookingAgents.length} selected booking agent(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedBookingAgents.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._bookingAgentService.BulkDelete(this.selectedBookingAgents).subscribe({
        next: () => {
          this.loadBookingAgents();
          this.selectedBookingAgents = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected booking agents have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting booking agents:', error);
          Swal.fire('Error!', 'Failed to delete selected booking agents.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
